/**
 * @studox/auth/invite — custom invitation links shared by the whole ecosystem.
 *
 * An invitation IS a StudoX code (which already encodes role/allowedApps/institution
 * server-side). A "custom invite link" simply carries that code to any app:
 *   https://core.studox.eu/?invite=A3KF-7NMP-2QVH
 *
 * The link only PRE-FILLS the code — real authority is always the DB code checked
 * server-side at /auth/login. So link params are display/convenience only and can
 * never grant access by themselves (tamper-safe by design).
 */

const CODE_RE = /[A-Z0-9]/g;

/** Normalize any raw code to canonical XXXX-XXXX-XXXX (uppercase, no ambiguous spaces). */
export function normalizeCode(raw) {
  if (!raw) return "";
  const chars = String(raw).toUpperCase().match(CODE_RE) || [];
  const s = chars.join("").slice(0, 12);
  return s.replace(/(.{4})(.{1,4})?(.{1,4})?/, (_, a, b, c) =>
    [a, b, c].filter(Boolean).join("-")
  );
}

/** Read an invite code from the current URL (?invite= or ?code=). Returns "" if none. */
export function readInviteCode(search) {
  try {
    const qs = new URLSearchParams(search ?? window.location.search);
    return normalizeCode(qs.get("invite") || qs.get("code") || "");
  } catch { return ""; }
}

/** Remove the invite param from the URL (so a refresh doesn't re-trigger it). */
export function clearInviteFromURL() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    url.searchParams.delete("code");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  } catch { /* noop */ }
}

/** Build a shareable invite link to any StudoX app for a given code. */
export function buildInviteLink(appBaseUrl, code) {
  const base = String(appBaseUrl || "https://core.studox.eu").replace(/\/+$/, "");
  return `${base}/?invite=${encodeURIComponent(normalizeCode(code))}`;
}

/** Is the given string a plausibly-complete StudoX code? (12 alphanumerics) */
export function isCompleteCode(raw) {
  return (String(raw).toUpperCase().match(CODE_RE) || []).length === 12;
}

export default { normalizeCode, readInviteCode, clearInviteFromURL, buildInviteLink, isCompleteCode };
